import { create } from 'zustand';

export type ThemeMode = 'auto' | 'light' | 'dark';

const STORAGE_KEY = 'sentinel.theme';

function readStored(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  } catch {
    /* storage unavailable */
  }
  return 'auto';
}

/** `data-theme` on <html>: absent = follow the device, "light"/"dark" = forced (design system §03). */
function apply(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'auto') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  /** Cycles auto → dark → light → auto. */
  cycle: () => void;
  isDark: () => boolean;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: readStored(),
  setMode(mode) {
    apply(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage unavailable */
    }
    set({ mode });
  },
  cycle() {
    const next: ThemeMode =
      get().mode === 'auto' ? 'dark' : get().mode === 'dark' ? 'light' : 'auto';
    get().setMode(next);
  },
  isDark() {
    const mode = get().mode;
    if (mode === 'auto') return window.matchMedia('(prefers-color-scheme: dark)').matches;
    return mode === 'dark';
  },
}));

apply(useThemeStore.getState().mode);
